import { all, fork, put, takeEvery, call } from 'redux-saga/effects';
import { SagaIterator } from '@redux-saga/core';

// apicore
import { APICore, setAuthorization, setWorkspaceIdHeader, clearAllAuthData } from '../../helpers/api/apiCore';

// helpers
import {
    login as loginApi,
    logout as logoutApi,
    signup as signupApi,
    forgotPassword as forgotPasswordApi,
} from '../../helpers/';

// actions
import { authApiResponseSuccess, authApiResponseError } from './actions';
import { resetWorkspace } from '../workspace/actions';

// constants
import { AuthActionTypes } from './constants';

interface UserData {
    payload: {
        username: string;
        password: string;
        name: string;
        email: string;
    };
    type: string;
}

const api = new APICore();

/**
 * Login the user
 * @param {*} payload - username and password
 */
function* login({ payload: { email, password }, type }: UserData): SagaIterator {
    try {
        const response = yield call(loginApi, { email, password });
        const data = response.data;

        // Store user session
        api.setLoggedInUser(data);
        setAuthorization(data['token']);

        // If user has exactly one workspace, auto-select it
        const workspaces = data['workspaces'] ?? [];
        if (workspaces.length === 1) {
            const ws = workspaces[0];
            sessionStorage.setItem('active_workspace', JSON.stringify(ws));
            localStorage.setItem('active_workspace', JSON.stringify(ws));
            // Inject X-Workspace-ID header for subsequent requests
            setWorkspaceIdHeader(ws.id);
            data['activeWorkspace'] = ws;
            data['needsWorkspaceSelection'] = false;
        } else {
            // Zero workspaces (new user) or several: the user must create/choose one first.
            sessionStorage.removeItem('active_workspace');
            localStorage.removeItem('active_workspace');
            setWorkspaceIdHeader(null);
            data['needsWorkspaceSelection'] = true;
        }

        yield put(authApiResponseSuccess(AuthActionTypes.LOGIN_USER, data));
    } catch (error: any) {
        yield put(authApiResponseError(AuthActionTypes.LOGIN_USER, error));
        api.setLoggedInUser(null);
        setAuthorization(null);
        setWorkspaceIdHeader(null);
    }
}

/**
 * Logout the user
 */
function* logout(): SagaIterator {
    try {
        yield call(logoutApi);
    } catch (error: any) {
        console.warn('Backend logout warning (proceeding with local cleanup):', error);
    } finally {
        api.setLoggedInUser(null);
        clearAllAuthData();
        yield put(resetWorkspace());
        yield put(authApiResponseSuccess(AuthActionTypes.LOGOUT_USER, {}));
    }
}

function* signup({ payload: { name, email, password } }: UserData): SagaIterator {
    try {
        const response = yield call(signupApi, { name, email, password });
        const user = response.data;
        yield put(authApiResponseSuccess(AuthActionTypes.SIGNUP_USER, user));
    } catch (error: any) {
        // error is already a message string
        const errorMessage = typeof error === 'string' ? error : 'Something went wrong';
        yield put(authApiResponseError(AuthActionTypes.SIGNUP_USER, errorMessage));
    }


}
function* forgotPassword({ payload: { email } }: UserData): SagaIterator {
    try {
        const response = yield call(forgotPasswordApi, { email });
        yield put(authApiResponseSuccess(AuthActionTypes.FORGOT_PASSWORD, response.data));
    } catch (error: any) {
        const errorMessage = typeof error === 'string' ? error : 'Something went wrong';
        yield put(authApiResponseError(AuthActionTypes.FORGOT_PASSWORD, errorMessage));
    }
}

export function* watchLoginUser() {
    yield takeEvery(AuthActionTypes.LOGIN_USER, login);
}

export function* watchLogout() {
    yield takeEvery(AuthActionTypes.LOGOUT_USER, logout);
}

export function* watchSignup(): any {
    yield takeEvery(AuthActionTypes.SIGNUP_USER, signup);
}

export function* watchForgotPassword(): any {
    yield takeEvery(AuthActionTypes.FORGOT_PASSWORD, forgotPassword);
}

function* authSaga() {
    yield all([fork(watchLoginUser), fork(watchLogout), fork(watchSignup), fork(watchForgotPassword)]);
}

export default authSaga;
